import isEqual from 'react-fast-compare';

import {
  Container,
  Grid,
  GridCellSummary,
  GridSummary,
  GridType,
  StorableSummary,
} from 'constants/SampleConstants';
import {GridCellProps} from 'samples/reduceSamplesUI';
import {formatGridPosition, isGrid} from 'samples/SampleUtil';
import GridCellKey, {getGridCellMoveOutKey} from 'test/lib/cypress/shared/GridCellKey';
import APIUtil from 'util/APIUtil';

import {Change, ChangeStats, MOVE_OUTCOMES} from './UploadContainersFlowConstants';

/**
 * Checks if a container was moved out of the grid
 *
 * A container was moved out if it no longer has a position
 */
function wasMovedOut(container: Container): boolean {
  return !container.gridCell;
}

/**
 * Checks if an affected grid was actually moved to a different position
 */
function didNotMove(
  container: Container,
  {parentStorable, gridCell}: {parentStorable: StorableSummary | null; gridCell?: GridCellSummary}
): boolean {
  return Boolean(
    parentStorable &&
    container.parentStorableId === parentStorable.id &&
    isEqual(container.gridCell, gridCell)
  );
}

/**
 * Checks if a container was moved in
 *
 * A container was moved in if its previous parent storable was null (meaning it was moved from root) or its
 * previous parent does not equal the current parent, the grid
 */
function wasMovedIn(
  prevParentStorable: StorableSummary | null,
  currParentStorable: StorableSummary
): boolean {
  return !prevParentStorable || prevParentStorable.id !== currParentStorable.id;
}

/**
 * Format the grid position for a container in a grid
 */
function formatContainerGridPosition(
  parentStorable: GridSummary,
  {rowIndex, columnIndex}: GridCellSummary
): string {
  const parentType = APIUtil.apiIdToType(parentStorable.id);
  return formatGridPosition({
    // Currently we assume that GridType.BOX/PLATE and ModelTypeConstants.BOX/PLATE are interchangable.
    gridType: parentType as unknown as GridType,
    rowIndex,
    columnIndex,
    width: parentStorable.storableSchema.width,
    includeNoun: true,
  });
}

/**
 * Format a container's parent storable
 */
function formatContainerParentStorable(
  parentStorable: StorableSummary | null,
  gridCell?: GridCellSummary
): string {
  if (!parentStorable) {
    return 'root';
  }
  if (isGrid(parentStorable)) {
    return `${formatContainerGridPosition(parentStorable, gridCell!)} of ${parentStorable.barcode}`;
  } else {
    return parentStorable.barcode || '';
  }
}

function nestedSet(
  object: Array<Array<GridCellProps>>,
  rowIndex: number,
  columnIndex: number,
  value: GridCellProps
): void {
  if (!object.hasOwnProperty(rowIndex)) {
    object[rowIndex] = [];
  }
  object[rowIndex][columnIndex] = value;
}

/**
 * Filter to just the changes involving updates to existing containers.
 * @param {array} changes Array of changes from upload containers validation
 * @returns Array of changes with create / noChange elements filtered out
 */
export function filterChangesToUpdatesOnly(changes: Array<Change>): Array<Change> {
  return changes.filter(
    (change) => change.previous != null && !didNotMove(change.container, change.previous)
  );
}

/**
 * Parse the changes returned by the upload containers validation
 * @param {Grid} gridPreview The grid shape for the uncommitted preview
 * @param {array} changes Array of changes from the upload containers validation
 * @returns An object with keys
 *    gridCellProps {Object} 2D mapping of grid cell props
 *    changeStats {Object} A count of ech change type
 */
export default function parseChanges(
  gridPreview: Grid,
  changes: Array<Change>
): {
  gridCellProps: Array<Array<GridCellProps>>;
  changeStats: ChangeStats;
} {
  const gridCellProps: Array<Array<GridCellProps>> = [];
  const changeStats: ChangeStats = {
    moveOut: 0,
    create: 0,
    moveIn: 0,
    reposition: 0,
    noChange: 0,
  };
  const orphanChanges: Array<Change> = [];
  changes.forEach((change) => {
    const {container} = change;

    if (wasMovedOut(container)) {
      orphanChanges.push(change);
      return;
    }

    let changeKey: keyof ChangeStats;
    let extraTooltipText: string | undefined;
    if (!change.previous) {
      changeKey = 'create';
    } else if (didNotMove(container, change.previous)) {
      changeKey = 'noChange';
    } else {
      const {parentStorable: prevParentStorable, gridCell: prevGridCell} = change.previous;
      const formattedGridCell = formatContainerGridPosition(gridPreview, container.gridCell!);
      if (wasMovedIn(prevParentStorable, gridPreview)) {
        changeKey = 'moveIn';
        const formattedPrevParent = formatContainerParentStorable(prevParentStorable, prevGridCell);
        extraTooltipText = `from ${formattedPrevParent} to ${formattedGridCell}`;
      } else {
        changeKey = 'reposition';
        const formattedPrevGridCell = formatContainerGridPosition(gridPreview, prevGridCell!);
        extraTooltipText = `from ${formattedPrevGridCell} to ${formattedGridCell}`;
      }
    }

    const singleGridCellProps: GridCellProps = {
      tooltipTitle: `${MOVE_OUTCOMES[changeKey].name}: ${container.barcode}`,
      innerClassName: MOVE_OUTCOMES[changeKey].gridCellClassName,
      dataTestAttrs: MOVE_OUTCOMES[changeKey].dataTestAttrs,
    };
    if (extraTooltipText) {
      singleGridCellProps.tooltipTitle += ` ${extraTooltipText}`;
    }
    changeStats[changeKey] += 1;

    const {rowIndex, columnIndex} = change.container.gridCell!;
    nestedSet(gridCellProps, rowIndex, columnIndex, singleGridCellProps);
  });

  orphanChanges.forEach((change) => {
    const {rowIndex, columnIndex} = change.previous!.gridCell!;
    const tooltipTitle = `${MOVE_OUTCOMES.moveOut.name}: ${change.container.barcode}`;
    const innerClassName = MOVE_OUTCOMES.moveOut.gridCellClassName;
    const dataTestAttrs = {key: GridCellKey.EMPTY_MOVE_OUT};

    if (gridCellProps.hasOwnProperty(rowIndex) && gridCellProps[rowIndex].hasOwnProperty(columnIndex)) {
      const singleGridCellProps = gridCellProps[rowIndex][columnIndex];
      singleGridCellProps.tooltipTitle += `\n${tooltipTitle}`;
      singleGridCellProps.innerClassName += ` ${innerClassName}`;
      singleGridCellProps.dataTestAttrs = {
        key: getGridCellMoveOutKey(singleGridCellProps.dataTestAttrs!.key! as GridCellKey),
      };
    } else {
      nestedSet(gridCellProps, rowIndex, columnIndex, {tooltipTitle, innerClassName, dataTestAttrs});
    }
  });
  changeStats.moveOut = orphanChanges.length;
  return {gridCellProps, changeStats};
}
